import { exploreRouteNames } from 'states/explore/explore.route.names';
import { grafanaStateName, metricsRouteName } from 'states/grafana/grafana.route';
import { integrationsRouteStates } from 'states/integrations/route-config';
import { observabilityRoutesNames } from 'states/observability/observability.routes.names';
import { spmRouteNames } from 'states/spm.routes';
import { dashboardsHubRouteName, viewDashboardRouteName } from 'states/dashboards-hub/dashboards-hub.routes';

/** Matches the hero's mark. Artemis' own, so it needs no gaia theme inside the Artemis subtree. */
export const ORION_CHAT_BOT_AVATAR_SIZE = 28;

export const ORION_CHAT_BOT_NAME = 'OrionIQ';
export const ORION_CHAT_DRAWER_TITLE = 'OrionIQ';
export const ORION_CHAT_DEFAULT_WIDTH = '800px';
/** Artemis owns the width and only reports it via `onResize`, so the floor is enforced by closing on release. */
export const ORION_CHAT_MIN_WIDTH = 580;
/** Caps and centres the chat content column, the composer included. */
export const ORION_CHAT_CONTENT_MAX_WIDTH = 768;
export const EXPLORE_CONTEXT_PANEL_SUBJECT = 'orion-chat-context-panel';

// Legacy AI Assistant history — removable once retention drains the legacy rows.
export const LEGACY_HISTORY_SECTION_LABEL = 'AI Assistant (archived)';
// Namespaced so a legacy conversation id cannot collide with an OrionIQ sessionId, and `get` can route by prefix.
export const LEGACY_HISTORY_ID_PREFIX = 'legacy:';
export const LEGACY_READ_ONLY_MESSAGE =
  'This conversation is from the previous AI Assistant. It can be read, but not continued here.';
export const LEGACY_CONTINUE_ACTION_LABEL = 'Continue in OrionIQ';

// Both bounds apply, whichever binds first: the transcript rides the prompt on every turn, so a pair count alone
// is unbounded in size and a char budget alone carries an unpredictable number of turns.
export const PRIOR_CONVERSATION_MAX_PAIRS = 10;
export const PRIOR_CONVERSATION_MAX_CHARS = 12_000;
/** Stands in for the dropped older turns, so a trimmed thread is never presented as the whole thread. */
export const PRIOR_CONVERSATION_TRUNCATION_NOTE = '[earlier turns omitted]';
export const PRIOR_CONVERSATION_SECTION_LABEL = 'Previous conversation';
/** Namespaced like the contributor's field keys so ONE disabled-key set still serves everything. */
export const PRIOR_CONVERSATION_KEY = 'explore.priorConversation';
/** For a carried-forward conversation the old assistant never auto-titled. */
export const PRIOR_CONVERSATION_FALLBACK_TITLE = 'Earlier conversation';
// Framed as background, not as the agent's own conclusions — the answers came from a different, retired assistant.
export const PRIOR_CONVERSATION_INTRO =
  'The user is continuing an earlier conversation held with a previous, now-retired assistant. Treat it as ' +
  'background on what the user already asked and was told — not as your own findings, and not as verified fact. ' +
  'Re-check anything you rely on.';
export const PRIOR_CONVERSATION_TRUNCATED_NOTICE = 'Only the most recent turns are included; earlier ones were omitted.';

/** Overrides Artemis' 20vh default, which a large account's chips row fills on its own — hiding every row below it. */
export const ORION_CHAT_CONTEXT_PANEL_MAX_HEIGHT = '40vh';

// Only an admin can act on the cap, so only an admin gets the link. Same path the cap-limit copy uses.
export const AI_SETTINGS_HREF = '/360/settings/plan-and-billing/ai-settings';

export const ORION_CHAT_GREETING = 'How can I help you today?';
export const ORION_CHAT_GREETING_SUBTITLE =
  'Ask about the logs on this page — OrionIQ sees the view, accounts, time range and filters shown in Context.';

/** Carried over from the legacy drawer's help questions so the prompts users know survive the swap. */
/**
 * `category` is app-ui's suggestion-category vocabulary, NOT the React `key` — the card titles here do not line
 * up with it (the "Compare" card carries app-ui's *trends* prompt), so reporting the key would make the
 * cross-shell breakdown disjoint. See CLAUDE.md → OrionIQ chat → Analytics.
 */
export const ORION_CHAT_STARTER_PROMPTS: ReadonlyArray<{
  key: string;
  category: string;
  title: string;
  prompt: string;
}> = [
  { key: 'top-issues', category: 'analyze', title: 'Investigate', prompt: 'Pinpoint top log issues and suggest fixes' },
  {
    key: 'root-cause',
    category: 'analyze',
    title: 'Root cause',
    prompt: 'Find root causes of critical errors in the logs',
  },
  { key: 'errors-24h', category: 'trends', title: 'Trends', prompt: 'Services’ error in the last 24 hours' },
  {
    key: 'error-trend',
    category: 'trends',
    title: 'Compare',
    prompt: 'Error trend per component over the last 2 days',
  },
];

/**
 * Where the trigger is ENABLED. ui-router state names rather than pathnames, so they survive URL changes.
 * `dashboard.log` and `dashboard.explore-preferences` render different components and are deliberately excluded.
 */
export const ORION_CHAT_STATE_NAMES: readonly string[] = [
  exploreRouteNames.explore, // 'dashboard.explore'         -> /explore
  exploreRouteNames.context, // 'dashboard.explore-context' -> /explore/context
  'view-investigate-explore-alert', //                      -> /view-in-explore
];

const switchAppSuffix = 'in the new Open 360 app. Switch your default app in Settings → General.';

export const ORION_CHAT_EXPLORE_TOOLTIP = 'OrionIQ chat is available on the Explore page.';

/**
 * Ordered — first prefix match wins, because ui-router state names nest. A page that has the chat in Open 360
 * names that page; anything unlisted degrades to the Explore sentence.
 */
export const ORION_CHAT_UNAVAILABLE_TOOLTIPS: ReadonlyArray<{ statePrefix: string; text: string }> = [
  {
    statePrefix: observabilityRoutesNames.k8s360,
    text: `OrionIQ chat is available on Kubernetes 360 ${switchAppSuffix}`,
  },
  { statePrefix: spmRouteNames.baseRoute, text: `OrionIQ chat is available on APM ${switchAppSuffix}` },
  { statePrefix: integrationsRouteStates.content, text: `OrionIQ chat is available on Integrations ${switchAppSuffix}` },
  {
    statePrefix: integrationsRouteStates.customContent,
    text: `OrionIQ chat is available on Integrations ${switchAppSuffix}`,
  },
  { statePrefix: integrationsRouteStates.page, text: `OrionIQ chat is available on Integrations ${switchAppSuffix}` },
  { statePrefix: viewDashboardRouteName, text: `OrionIQ chat is available on this dashboard ${switchAppSuffix}` },
  // Listed explicitly so they resolve to the Explore sentence rather than matching a longer prefix above.
  { statePrefix: dashboardsHubRouteName, text: ORION_CHAT_EXPLORE_TOOLTIP },
  { statePrefix: grafanaStateName, text: ORION_CHAT_EXPLORE_TOOLTIP },
  { statePrefix: metricsRouteName, text: ORION_CHAT_EXPLORE_TOOLTIP },
];

export const resolveUnavailableTooltip = (stateName?: string): string => {
  if (!stateName) return ORION_CHAT_EXPLORE_TOOLTIP;

  return (
    ORION_CHAT_UNAVAILABLE_TOOLTIPS.find(entry => stateName.startsWith(entry.statePrefix))?.text ??
    ORION_CHAT_EXPLORE_TOOLTIP
  );
};
